import React from "react";
import { Check, X } from "lucide-react";
import { Section, Reveal, Glass, Mono } from "./NetworkAtoms";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const INTRODUCER = [
  "Real relationships with business owners — not a bought list",
  "A professional introduction source: broker, accountant, consultant, dealer, agency",
  "Honest case notes, including the awkward parts of the story",
  "Happy to work through a portal rather than a phone call",
  "Wants clean, documented commission — not a handshake",
];

const LENDER = [
  "Clear criteria you're willing to publish to the router",
  "Decisions in days, not weeks — and a desk that answers",
  "You fund what you say you fund",
  "You'll treat an introducer's client like your own",
  "Willing to sign a mutual NDA and a partner agreement",
];

const DECLINE = [
  "Bought or scraped data, and anything without genuine consent",
  "Recycled files already shopped around the market",
  "Undisclosed fees loaded onto the client at the last minute",
  "Anyone who won't put their criteria or their terms in writing",
  "Volume for the sake of volume — it costs everyone money",
];

export default function PartnerCriteria() {
  // Held at whisper on purpose: the loud field belongs to the hero band. Same Hub switch.
  const rain = useCodeRain("lending_network", "dark");
  return (
    <div className="relative overflow-hidden" data-testid="network-criteria-band">
      {rain.on && <CodeRain variant="dark" intensity="whisper" />}
      <Section eyebrow="Who we're looking for" testId="network-criteria"
               title="We'd rather be honest now than disappoint you later."
               lede="The network works because both sides are chosen carefully. Here's exactly what we look for — and what gets a polite no.">
      <div className="mt-12 grid lg:grid-cols-2 gap-5">
        <Reveal>
          <Glass className="h-full p-7" testId="criteria-introducer">
            <Mono>A good introducer</Mono>
            <ul className="mt-5 space-y-3.5">
              {INTRODUCER.map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-0.5 grid place-items-center size-5 rounded-full bg-emerald-500/15 border border-emerald-400/30 shrink-0">
                    <Check className="size-3 text-emerald-400" strokeWidth={3} />
                  </span>
                  <span className="text-[14px] leading-relaxed text-slate-300">{t}</span>
                </li>
              ))}
            </ul>
          </Glass>
        </Reveal>

        <Reveal delay={0.08}>
          <Glass className="h-full p-7" testId="criteria-lender">
            <Mono>A good lender partner</Mono>
            <ul className="mt-5 space-y-3.5">
              {LENDER.map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-0.5 grid place-items-center size-5 rounded-full bg-emerald-500/15 border border-emerald-400/30 shrink-0">
                    <Check className="size-3 text-emerald-400" strokeWidth={3} />
                  </span>
                  <span className="text-[14px] leading-relaxed text-slate-300">{t}</span>
                </li>
              ))}
            </ul>
          </Glass>
        </Reveal>
      </div>

      <Reveal delay={0.12}>
        <div className="mt-5 rounded-2xl border border-red-400/20 bg-red-500/[0.05] p-7" data-testid="criteria-decline">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-red-300/90">What we decline</span>
          <ul className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3.5">
            {DECLINE.map((t) => (
              <li key={t} className="flex gap-3">
                <span className="mt-0.5 grid place-items-center size-5 rounded-full bg-red-500/15 border border-red-400/30 shrink-0">
                  <X className="size-3 text-red-300" strokeWidth={3} />
                </span>
                <span className="text-[13.5px] leading-relaxed text-slate-400">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
      </Section>
    </div>
  );
}
